'use client'

import { useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useDirectoryFileUploadContext } from '@/features/drive/functionalities/upload/directory-file-upload-context'
import { IDynamicIslandItemProps } from '@/features/dynamic-island/dynamic-island-item'
import { useDynamicIslandContext } from '@/features/dynamic-island/dynamic-island-context'

export const DirectoryFileUploadDynamicIsland = () => {
    const { setState } = useDynamicIslandContext()
    const { fileUploadState } = useDirectoryFileUploadContext()

    const dynamicIslandState = useMemo<Omit<IDynamicIslandItemProps<typeof fileUploadState.state>, 'id'>>(() => {
        return {
            state: fileUploadState.state,
            stateMap: {
                idle: 'idle',
                uploading: 'content',
                done: 'final-remark',
            },
            icon: fileUploadState.state === 'uploading' ? 'loading' : 'check',
            render: ({ completedWidthAnimation }) => {
                const uploadingFileNumber =
                    'uploaded' in fileUploadState
                        ? Math.min(fileUploadState.uploaded + fileUploadState.failed + 1, fileUploadState.numberOfFiles)
                        : 1

                const spaces = String(uploadingFileNumber)
                    .split('')
                    .reduce((acc, item, index, arr) => {
                        const isDecimalOne = item === '1' && arr.length === 2 && index !== arr.length - 1

                        return acc + (isDecimalOne ? 0.5 : 1)
                    }, 0)

                const width = `${spaces}ch`

                if (
                    fileUploadState.state === 'uploading' ||
                    (!completedWidthAnimation && fileUploadState.state === 'done')
                ) {
                    return (
                        <div>
                            {`Uploading files ... `}{' '}
                            <motion.div
                                className="inline-block"
                                initial={{
                                    width: '1ch',
                                    minWidth: '1ch',
                                    maxWidth: '1ch',
                                }}
                                animate={{
                                    width,
                                    minWidth: width,
                                    maxWidth: width,
                                }}
                                transition={{
                                    width: { duration: 0.07, ease: 'linear' },
                                }}
                            >
                                <motion.div
                                    initial={{ opacity: 0.8 }}
                                    animate={{ opacity: 1 }}
                                    key={'directory-upload-progress-counter' + uploadingFileNumber}
                                >
                                    {uploadingFileNumber}{' '}
                                </motion.div>
                            </motion.div>{' '}
                            {`/ ${fileUploadState.numberOfFiles}`}
                        </div>
                    )
                } else if (fileUploadState.state === 'done') {
                    return (
                        <div>
                            {fileUploadState.failed > 0
                                ? `Uploaded ${fileUploadState.uploaded}; ${fileUploadState.failed} failed`
                                : `Successfully uploaded ${fileUploadState.uploaded} ${
                                      fileUploadState.uploaded === 1 ? 'file' : 'files'
                                  }`}
                        </div>
                    )
                }

                return null
            },
        }
    }, [fileUploadState])

    useEffect(() => {
        setState<typeof fileUploadState.state>({ id: 'file-upload' }, () => ({
            ...dynamicIslandState,
            id: 'file-upload',
            priority: 1,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicIslandState])

    return null
}
